import { getSupabase } from './client';
import { requireUserId } from './auth';
import { getJson, isS3Configured } from '../storage/s3';
import {
  openCompanyRow,
  openMeetingRow,
  openMeetingRows,
  openParticipantRows,
  openPersonRow,
  openTranscriptSegments,
} from '../crypto/openContent';
import { contentSealer } from '../crypto/sealContent';
import { getIndexer, type Indexer } from '../crypto/blindIndex';
import { t } from '../language';
import { normalizePersonName, pickNameMatch, samePersonName } from '../directoryRules';
import type {
  CompanyDTO,
  CompanyProfileDTO,
  CompanyUpdate,
  MeetingParticipantDTO,
  PersonDTO,
  PersonProfileDTO,
  PersonUpdate,
  PersonUpdateResult,
  ProfileActionItemDTO,
  ProfileDecisionDTO,
  ProfileMeetingDTO,
  TranscriptExcerptDTO,
  TranscriptSegmentDTO,
} from '../../shared/types';

/**
 * Kişiler ve şirketler (people / companies tabloları, bkz.
 * migrations/0012_people_companies.sql).
 *
 * Eşleme kuralları:
 *  - E-postası olan katılımcı (takvimden gelen) yalnızca e-postayla kişiye
 *    bağlanır, yoksa e-postasıyla yeni kişi açılır; şirketi e-posta
 *    domain'inden bulunur ya da oluşturulur.
 *  - E-postası olmayan katılımcı (linkten katılan / botun gördüğü ad) ayrı
 *    bir profildir: yalnızca e-postasız ve adı birebir (harf duyarlı) aynı
 *    kişiye yazılır, yoksa yeni kişi açılır. Kurallar: ../directoryRules.ts.
 *
 * Erişim denetimi RLS'te (user_id = auth.uid()).
 *
 * Şifreleme (veri koruma programı Faz 4, 3. seviye): ad, e-posta, rol, alan adı, not ve
 * katılımcı alanları tn1 ile şifreli yazılabilir — kişi ve şirkette bağlam satırın kendi
 * kimliği (bu yüzden satır kimliği burada üretiliyor), katılımcıda toplantı. Eşleştirme
 * arama özetleriyle (`*_hash`, ../crypto/blindIndex.ts); özeti henüz olmayan eski düz
 * kayıtlar için düz değerle de bakılır. Okunan her satır çözülür.
 */

interface Directory {
  userId: string;
  /** Kullanıcının anahtarı yoksa null: yalnızca düz eşleştirme, özet yazılmaz. */
  index: Indexer | null;
}

async function directory(): Promise<Directory> {
  const userId = await requireUserId();
  return { userId, index: await getIndexer(userId) };
}

const PERSON_COLUMNS = 'id, name, email, role, company_id, created_at';

/** Kişi satırına yazılacak sütunlar: değerler (seviyeye göre) şifreli, özetler yanında. */
async function personColumns(
  dir: Directory,
  personId: string,
  values: { name?: string; email?: string | null; role?: string | null }
): Promise<Record<string, unknown>> {
  const sealer = await contentSealer(dir.userId, personId);
  const out: Record<string, unknown> = {};
  if (values.name !== undefined) {
    out.name = await sealer.field('people.name', values.name);
    out.name_hash = dir.index ? await dir.index('person-name', values.name) : null;
  }
  if (values.email !== undefined) {
    out.email = await sealer.field('people.email', values.email);
    out.email_hash = dir.index ? await dir.index('email', values.email) : null;
  }
  if (values.role !== undefined) out.role = await sealer.field('people.role', values.role);
  return out;
}

async function companyColumns(
  dir: Directory,
  companyId: string,
  values: { name?: string; domain?: string | null; notes?: string | null }
): Promise<Record<string, unknown>> {
  const sealer = await contentSealer(dir.userId, companyId);
  const out: Record<string, unknown> = {};
  if (values.name !== undefined) {
    out.name = await sealer.field('companies.name', values.name);
    out.name_hash = dir.index ? await dir.index('company-name', values.name) : null;
  }
  if (values.domain !== undefined) {
    out.domain = await sealer.field('companies.domain', values.domain);
    out.domain_hash = dir.index ? await dir.index('company-domain', values.domain) : null;
  }
  if (values.notes !== undefined) out.notes = await sealer.field('companies.notes', values.notes);
  return out;
}

/** Önce arama özetiyle, bulunamazsa özeti olmayan (eski, düz) satırlarda düz değerle. */
async function findByIndex<T extends Record<string, any>>(
  dir: Directory,
  table: 'people' | 'companies',
  columns: string,
  hashColumn: string,
  hash: string | null,
  plain: (query: any) => any,
  refine: (query: any) => any = (query) => query
): Promise<T[]> {
  const supabase = getSupabase();
  const found: T[] = [];
  if (hash) {
    const { data, error } = await refine(supabase.from(table).select(columns).eq('user_id', dir.userId).eq(hashColumn, hash));
    if (error) throw new Error(error.message);
    found.push(...((data ?? []) as T[]));
  }
  if (found.length === 0) {
    const { data, error } = await refine(plain(supabase.from(table).select(columns).eq('user_id', dir.userId).is(hashColumn, null)));
    if (error) throw new Error(error.message);
    found.push(...((data ?? []) as T[]));
  }
  const open = table === 'people' ? openPersonRow : openCompanyRow;
  return Promise.all(found.map((row) => open(row, dir.userId))) as Promise<T[]>;
}

/** Kişisel e-posta sağlayıcıları — bunların domain'i bir "şirket" değil. */
const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.com.tr', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'yandex.com', 'yandex.com.tr', 'yandex.ru', 'proton.me', 'protonmail.com', 'gmx.com',
  'gmx.de', 'mail.com', 'zoho.com', 'hotmail.com.tr', 'outlook.com.tr', 'windowslive.com',
]);

/** İkinci düzey genel uzantılar — `acme.com.tr`'de şirket adı "acme". */
const SECOND_LEVEL_SUFFIXES = new Set([
  'com', 'net', 'org', 'gov', 'edu', 'co', 'ac', 'bel', 'k12', 'gen', 'biz', 'info', 'av',
]);

function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}

function normalizeEmail(email: string | undefined | null): string | null {
  const trimmed = email?.trim().toLowerCase();
  return trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? trimmed : null;
}

export function companyDomainFromEmail(email: string): string | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain || FREE_MAIL_DOMAINS.has(domain)) return null;
  return domain;
}

/** `acme.com` → "Acme", `bilgi.edu.tr` → "Bilgi". */
export function companyNameFromDomain(domain: string): string {
  const labels = domain.split('.').filter(Boolean);
  let core = labels[0] ?? domain;
  if (labels.length >= 3 && SECOND_LEVEL_SUFFIXES.has(labels[labels.length - 2])) {
    core = labels[labels.length - 3];
  } else if (labels.length >= 2) {
    core = labels[labels.length - 2];
  }
  return core.charAt(0).toLocaleUpperCase('tr') + core.slice(1);
}

// ————————————————————————————————————————————————
// Katılımcı → kişi / şirket eşleme
// ————————————————————————————————————————————————

interface PersonRow {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  company_id: string | null;
}

async function findCompany(dir: Directory, by: 'domain' | 'name', value: string): Promise<string | null> {
  const kind = by === 'domain' ? 'company-domain' : 'company-name';
  const rows = await findByIndex<{ id: string }>(
    dir,
    'companies',
    'id',
    `${by}_hash`,
    dir.index ? await dir.index(kind, value) : null,
    (query) => query.ilike(by, escapeLike(value)),
    (query) => query.limit(1)
  );
  return rows[0]?.id ?? null;
}

async function ensureCompanyForDomain(dir: Directory, domain: string): Promise<string | null> {
  const existing = await findCompany(dir, 'domain', domain);
  if (existing) return existing;

  const name = companyNameFromDomain(domain);
  const id = crypto.randomUUID();
  const inserted = await getSupabase()
    .from('companies')
    .insert({ id, user_id: dir.userId, ...(await companyColumns(dir, id, { name, domain })) })
    .select('id')
    .single();
  if (inserted.data) return inserted.data.id as string;

  // Aynı adla (farklı domain) bir şirket zaten varsa ona bağla.
  const byName = await findCompany(dir, 'name', name);
  if (byName) return byName;

  console.error('[directory] şirket oluşturulamadı:', inserted.error?.message);
  return null;
}

async function findPersonByEmail(dir: Directory, email: string, excludeId?: string): Promise<PersonRow | null> {
  const rows = await findByIndex<PersonRow>(
    dir,
    'people',
    PERSON_COLUMNS,
    'email_hash',
    dir.index ? await dir.index('email', email) : null,
    (query) => query.ilike('email', escapeLike(email)),
    (query) => (excludeId ? query.neq('id', excludeId) : query).limit(1)
  );
  return rows[0] ?? null;
}

async function findOrCreatePerson(dir: Directory, participant: MeetingParticipantDTO): Promise<PersonRow | null> {
  const supabase = getSupabase();
  const email = normalizeEmail(participant.email);
  const name = normalizePersonName(participant.name) || (email ? email.split('@')[0] : '');
  if (!name) return null;

  if (participant.personId) {
    const byId = await supabase
      .from('people')
      .select(PERSON_COLUMNS)
      .eq('user_id', dir.userId)
      .eq('id', participant.personId)
      .maybeSingle();
    if (byId.data) return openPersonRow(byId.data as PersonRow & Record<string, unknown>, dir.userId);
  }

  if (email) {
    const person = await findPersonByEmail(dir, email);
    if (person) {
      // Önceden isimle açılmış kişiye şirket bağı yoksa domain'den tamamla.
      if (!person.company_id) {
        const domain = companyDomainFromEmail(email);
        const companyId = domain ? await ensureCompanyForDomain(dir, domain) : null;
        if (companyId) {
          await supabase.from('people').update({ company_id: companyId }).eq('id', person.id);
          person.company_id = companyId;
        }
      }
      return person;
    }
  }

  // E-postasız katılımcı: adı birebir aynı, e-postasız kişiye yazılır (en eskisi).
  if (!email) {
    const candidates = await findByIndex<PersonRow & { created_at: string }>(
      dir,
      'people',
      PERSON_COLUMNS,
      'name_hash',
      dir.index ? await dir.index('person-name', name) : null,
      (query) => query.eq('name', name),
      (query) => query.is('email', null).order('created_at').limit(5)
    );
    const nameMatch = pickNameMatch(candidates, name, email);
    if (nameMatch) return nameMatch;
  }

  const domain = email ? companyDomainFromEmail(email) : null;
  const companyId = domain ? await ensureCompanyForDomain(dir, domain) : null;
  const id = crypto.randomUUID();
  const role = participant.role?.trim() || null;
  const inserted = await supabase
    .from('people')
    .insert({ id, user_id: dir.userId, ...(await personColumns(dir, id, { name, email, role })), company_id: companyId })
    .select('id')
    .single();

  if (inserted.error || !inserted.data) {
    console.error('[directory] kişi oluşturulamadı:', inserted.error?.message);
    return null;
  }
  return { id, name, email, role, company_id: companyId };
}

/**
 * Toplantının katılımcılarını kişilere bağlayarak yazar.
 * `replace`: liste tamdır, eskiler silinir (arayüzden gelen düzenleme).
 * `merge`: yalnızca toplantıda henüz olmayan kişiler eklenir (bot sonucu).
 */
export async function resolveParticipants(
  meetingId: string,
  participants: MeetingParticipantDTO[],
  mode: 'replace' | 'merge' = 'replace'
): Promise<void> {
  const supabase = getSupabase();
  const dir = await directory();
  const userId = dir.userId;

  let existingPersonIds = new Set<string>();
  let existingNames: string[] = [];
  let startPosition = 0;
  if (mode === 'merge') {
    const { data } = await supabase
      .from('meeting_participants')
      .select('person_id, name, position')
      .eq('meeting_id', meetingId);
    const rows = await openParticipantRows((data ?? []) as Array<{ person_id: string | null; name: string; position: number }>, userId, meetingId);
    existingPersonIds = new Set(rows.map((r) => r.person_id).filter((id): id is string => Boolean(id)));
    existingNames = rows.map((r) => r.name);
    startPosition = rows.reduce((max, r) => Math.max(max, r.position + 1), 0);
  }

  const resolved: Array<{ participant: MeetingParticipantDTO; person: PersonRow | null }> = [];
  for (const participant of participants) {
    if (!participant?.name?.trim() && !participant?.email) continue;
    // Toplantıda aynı adla zaten olan katılımcıyı (ör. takvimden e-postasıyla
    // gelmiş) bottan tekrar ekleme — ayrı, e-postasız bir kişi de açılmasın.
    if (
      mode === 'merge' &&
      !participant.personId &&
      existingNames.some((existing) => samePersonName(existing, participant.name))
    ) {
      continue;
    }
    resolved.push({ participant, person: await findOrCreatePerson(dir, participant) });
  }

  if (mode === 'replace') {
    await supabase.from('meeting_participants').delete().eq('meeting_id', meetingId);
  }

  const seen = new Set<string>(existingPersonIds);
  const companyNames = new Map<string, string>();
  const companyIds = [...new Set(resolved.map((r) => r.person?.company_id).filter(Boolean))] as string[];
  if (companyIds.length > 0) {
    const { data } = await supabase.from('companies').select('id, name').in('id', companyIds);
    for (const row of (data ?? []) as Array<{ id: string; name: string }>) {
      companyNames.set(row.id, (await openCompanyRow(row, userId)).name);
    }
  }

  const plainRows = resolved
    .filter(({ person }) => {
      if (!person) return mode === 'replace';
      if (seen.has(person.id)) return false;
      seen.add(person.id);
      return true;
    })
    .map(({ participant, person }, index) => ({
      meeting_id: meetingId,
      person_id: person?.id ?? null,
      name: person?.name ?? normalizePersonName(participant.name),
      email: person?.email ?? normalizeEmail(participant.email),
      role: participant.role?.trim() || person?.role || null,
      company:
        participant.company?.trim() ||
        (person?.company_id ? companyNames.get(person.company_id) ?? null : null),
      position: startPosition + index,
    }));

  if (plainRows.length === 0) return;
  // Katılımcı alanları toplantı bağlamında şifrelenir (3. seviye).
  const sealer = await contentSealer(userId, meetingId);
  const rows = await Promise.all(
    plainRows.map(async (row) => ({
      ...row,
      name: (await sealer.field('meeting_participants.name', row.name)) ?? row.name,
      email: await sealer.field('meeting_participants.email', row.email),
      role: await sealer.field('meeting_participants.role', row.role),
      company: await sealer.field('meeting_participants.company', row.company),
    }))
  );
  const { error } = await supabase.from('meeting_participants').insert(rows);
  if (error) console.error('[directory] katılımcılar yazılamadı:', error.message);
}

// ————————————————————————————————————————————————
// Listeler
// ————————————————————————————————————————————————

interface MeetingRef {
  meeting_id: string;
  meetings: { recorded_at: string | null; created_at: string; deleted_at: string | null } | null;
}

function meetingStats(refs: MeetingRef[]): { meetingCount: number; lastMeetingISO?: string } {
  const ids = new Set<string>();
  let last: string | undefined;
  for (const ref of refs) {
    const meeting = Array.isArray(ref.meetings) ? ref.meetings[0] : ref.meetings;
    if (!meeting || meeting.deleted_at) continue;
    ids.add(ref.meeting_id);
    const when = meeting.recorded_at ?? meeting.created_at;
    if (!last || when > last) last = when;
  }
  return { meetingCount: ids.size, lastMeetingISO: last };
}

export async function listPeople(): Promise<PersonDTO[]> {
  const userId = await requireUserId();
  // Sıralama çözüldükten sonra (şifreli adla sıralanamaz).
  const { data, error } = await getSupabase()
    .from('people')
    .select(`
      id, name, email, role, company_id,
      companies ( id, name ),
      meeting_participants ( meeting_id, meetings ( recorded_at, created_at, deleted_at ) )
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('[directory] listPeople:', error.message);
    throw new Error(t('Kişiler yüklenemedi.', 'Could not load people.'));
  }

  const rows = await Promise.all((data ?? []).map((row) => openPersonRow(row as Record<string, unknown>, userId)));
  return rows
    .map((row: any) => ({
      id: row.id,
      name: row.name,
      email: row.email ?? undefined,
      role: row.role ?? undefined,
      companyId: row.company_id ?? undefined,
      companyName: (Array.isArray(row.companies) ? row.companies[0] : row.companies)?.name ?? undefined,
      ...meetingStats(row.meeting_participants ?? []),
    }))
    .sort((a, b) => (b.lastMeetingISO ?? '').localeCompare(a.lastMeetingISO ?? '') || a.name.localeCompare(b.name, 'tr'));
}

export async function listCompanies(): Promise<CompanyDTO[]> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('companies')
    .select(`
      id, name, domain, notes,
      people ( id, meeting_participants ( meeting_id, meetings ( recorded_at, created_at, deleted_at ) ) )
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('[directory] listCompanies:', error.message);
    throw new Error(t('Şirketler yüklenemedi.', 'Could not load companies.'));
  }

  const rows = await Promise.all((data ?? []).map((row) => openCompanyRow(row as Record<string, unknown>, userId)));
  return rows
    .map((row: any) => {
      const people = (row.people ?? []) as Array<{ id: string; meeting_participants: MeetingRef[] }>;
      return {
        id: row.id,
        name: row.name,
        domain: row.domain ?? undefined,
        notes: row.notes ?? undefined,
        peopleCount: people.length,
        ...meetingStats(people.flatMap((p) => p.meeting_participants ?? [])),
      };
    })
    .sort((a, b) => (b.lastMeetingISO ?? '').localeCompare(a.lastMeetingISO ?? '') || a.name.localeCompare(b.name, 'tr'));
}

// ————————————————————————————————————————————————
// Profiller
// ————————————————————————————————————————————————

interface MeetingRow {
  id: string;
  title: string;
  recorded_at: string | null;
  created_at: string;
  deleted_at: string | null;
  summary: string | null;
  key_points: unknown;
  options: any;
  action_items: Array<{ id: string; text: string; assignee: string | null; is_done: boolean; due_date: string | null }>;
  meeting_participants: Array<{ name: string; person_id: string | null }>;
}

const MEETING_SELECT = `
  id, title, recorded_at, created_at, deleted_at, summary, key_points, options,
  action_items ( id, text, assignee, is_done, due_date ),
  meeting_participants ( name, person_id )
`;

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

function toProfileMeeting(row: MeetingRow): ProfileMeetingDTO {
  const options = row.options ?? {};
  return {
    id: row.id,
    title: row.title,
    recordedAtISO: row.recorded_at ?? row.created_at,
    tldr: row.summary ?? '',
    keyPoints: strings(row.key_points),
    decisions: strings(options.decisions),
    hasAudio: Boolean(options.s3?.audioKey || options.audioFilePath),
    hasTranscript: Boolean(options.s3?.transcriptKey) || !options.s3,
    participantNames: (row.meeting_participants ?? []).map((p) => p.name),
  };
}

async function loadMeetings(meetingIds: string[]): Promise<MeetingRow[]> {
  if (meetingIds.length === 0) return [];
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select(MEETING_SELECT)
    .eq('user_id', userId)
    .in('id', meetingIds)
    .is('deleted_at', null)
    .order('recorded_at', { ascending: false, nullsFirst: false });
  if (error) {
    console.error('[directory] toplantılar okunamadı:', error.message);
    return [];
  }
  return (await openMeetingRows((data ?? []) as unknown as MeetingRow[], userId)) as MeetingRow[];
}

function collectDecisions(meetings: MeetingRow[]): ProfileDecisionDTO[] {
  return meetings.flatMap((m) =>
    strings(m.options?.decisions).map((text) => ({
      meetingId: m.id,
      meetingTitle: m.title,
      recordedAtISO: m.recorded_at ?? m.created_at,
      text,
    }))
  );
}

function toActionItem(meeting: MeetingRow, item: MeetingRow['action_items'][number]): ProfileActionItemDTO {
  return {
    id: item.id,
    meetingId: meeting.id,
    meetingTitle: meeting.title,
    text: item.text,
    assignee: item.assignee ?? undefined,
    completed: item.is_done,
    dueDate: item.due_date ?? undefined,
  };
}

async function loadTranscript(meeting: MeetingRow): Promise<TranscriptSegmentDTO[]> {
  const s3 = meeting.options?.s3;
  if (s3?.transcriptKey) {
    if (!isS3Configured()) return [];
    return getJson<TranscriptSegmentDTO[]>(s3.transcriptKey).catch(() => []);
  }
  const { data } = await getSupabase()
    .from('transcripts')
    .select('segments')
    .eq('meeting_id', meeting.id)
    .maybeSingle();
  const segments = await openTranscriptSegments(data?.segments, await requireUserId(), meeting.id);
  return Array.isArray(segments) ? (segments as TranscriptSegmentDTO[]) : [];
}

const EXCERPT_MEETINGS = 10;
const EXCERPTS_PER_MEETING = 20;

export async function getPersonProfile(personId: string): Promise<PersonProfileDTO> {
  const supabase = getSupabase();
  const { data: row, error } = await supabase
    .from('people')
    .select(`
      id, name, email, role, company_id, companies ( id, name ),
      meeting_participants ( meeting_id, meetings ( recorded_at, created_at, deleted_at ) )
    `)
    .eq('id', personId)
    .maybeSingle();

  if (error || !row) throw new Error(t('Kişi bulunamadı.', 'Person not found.'));

  const r = (await openPersonRow(row as Record<string, unknown>, await requireUserId())) as any;
  const refs = (r.meeting_participants ?? []) as MeetingRef[];
  const person: PersonDTO = {
    id: r.id,
    name: r.name,
    email: r.email ?? undefined,
    role: r.role ?? undefined,
    companyId: r.company_id ?? undefined,
    companyName: (Array.isArray(r.companies) ? r.companies[0] : r.companies)?.name ?? undefined,
    ...meetingStats(refs),
  };

  const meetings = await loadMeetings([...new Set(refs.map((ref) => ref.meeting_id))]);
  const nameKey = normalizePersonName(person.name);

  // Kişiye atanmış aksiyonlar: atanan adı kişinin adıyla birebir aynı olanlar
  // ("Ahmet"e atanan iş "Ahmet Yılmaz"ın profilinde görünmez).
  const actionItems = meetings.flatMap((m) =>
    (m.action_items ?? [])
      .filter((item) => samePersonName(item.assignee, person.name))
      .map((item) => toActionItem(m, item))
  );

  // Transkript alıntıları: konuşmacı etiketi kişinin adı ya da kişiye eşlenmiş etiket.
  const excerpts: TranscriptExcerptDTO[] = [];
  for (const meeting of meetings.slice(0, EXCERPT_MEETINGS)) {
    const speakerMap = (meeting.options?.speakerMap ?? {}) as Record<string, string>;
    const labels = new Set(
      Object.entries(speakerMap)
        .filter(([, id]) => id === personId)
        .map(([label]) => normalizePersonName(label))
    );
    labels.add(nameKey);

    const segments = await loadTranscript(meeting);
    segments
      .filter((s) => labels.has(normalizePersonName(s.speaker)))
      .slice(0, EXCERPTS_PER_MEETING)
      .forEach((s) =>
        excerpts.push({
          meetingId: meeting.id,
          meetingTitle: meeting.title,
          recordedAtISO: meeting.recorded_at ?? meeting.created_at,
          seconds: s.seconds,
          timestamp: s.timestamp,
          text: s.text,
        })
      );
  }

  return {
    person,
    meetings: meetings.map(toProfileMeeting),
    decisions: collectDecisions(meetings),
    actionItems,
    excerpts,
  };
}

export async function getCompanyProfile(companyId: string): Promise<CompanyProfileDTO> {
  const supabase = getSupabase();
  const { data: row, error } = await supabase
    .from('companies')
    .select(`
      id, name, domain, notes,
      people ( id, name, email, role, company_id,
        meeting_participants ( meeting_id, meetings ( recorded_at, created_at, deleted_at ) ) )
    `)
    .eq('id', companyId)
    .maybeSingle();

  if (error || !row) throw new Error(t('Şirket bulunamadı.', 'Company not found.'));

  const r = (await openCompanyRow(row as Record<string, unknown>, await requireUserId())) as any;
  const peopleRows = (r.people ?? []) as Array<any>;
  const people: PersonDTO[] = peopleRows.map((p) => ({
    id: p.id,
    name: p.name,
    email: p.email ?? undefined,
    role: p.role ?? undefined,
    companyId: companyId,
    companyName: r.name,
    ...meetingStats(p.meeting_participants ?? []),
  }));
  const refs = peopleRows.flatMap((p) => (p.meeting_participants ?? []) as MeetingRef[]);

  const company: CompanyDTO = {
    id: r.id,
    name: r.name,
    domain: r.domain ?? undefined,
    notes: r.notes ?? undefined,
    peopleCount: people.length,
    ...meetingStats(refs),
  };

  const meetings = await loadMeetings([...new Set(refs.map((ref) => ref.meeting_id))]);
  return {
    company,
    people: people.sort((a, b) => b.meetingCount - a.meetingCount),
    meetings: meetings.map(toProfileMeeting),
    decisions: collectDecisions(meetings),
    actionItems: meetings.flatMap((m) =>
      (m.action_items ?? []).filter((item) => !item.is_done).map((item) => toActionItem(m, item))
    ),
  };
}

/** Şirket profilindeki toplantıların kimlikleri — dışa aktarım için. */
export async function getCompanyMeetingIds(companyId: string): Promise<string[]> {
  const profile = await getCompanyProfile(companyId);
  return profile.meetings.map((m) => m.id);
}

// ————————————————————————————————————————————————
// Düzenleme
// ————————————————————————————————————————————————

/**
 * Kişiyi günceller. Girilen e-posta başka bir kişide kayıtlıysa iki kayıt
 * otomatik birleşir: o kaydın toplantıları bu kişiye taşınır, o kayıt silinir
 * (ör. linkten katılmış e-postasız "Ahmet Yılmaz"a e-posta girilince takvimden
 * gelen profiliyle tek kişi olur). Birleşilen kayıt sonuçta döner.
 */
export async function updatePerson(personId: string, patch: PersonUpdate): Promise<PersonUpdateResult> {
  const values: { name?: string; email?: string | null; role?: string | null } = {};
  let companyId: string | null | undefined;
  if (typeof patch.name === 'string' && normalizePersonName(patch.name)) values.name = normalizePersonName(patch.name);
  if (patch.email !== undefined) {
    const email = normalizeEmail(patch.email);
    if (patch.email?.trim() && !email) throw new Error(t('Geçerli bir e-posta adresi girin.', 'Enter a valid email address.'));
    values.email = email;
  }
  if (patch.role !== undefined) values.role = patch.role?.trim() || null;
  if (patch.companyId !== undefined) companyId = patch.companyId || null;
  if (Object.keys(values).length === 0 && companyId === undefined) return {};

  const supabase = getSupabase();
  const dir = await directory();
  const result: PersonUpdateResult = {};

  const email = values.email;
  const before = email
    ? await supabase.from('people').select('id, email').eq('id', personId).maybeSingle().then(async ({ data }) =>
        data ? openPersonRow(data as { id: string; email: string | null }, dir.userId) : null
      )
    : null;
  // Yalnızca yeni girilen / değişen e-postada birleştir ve şirketi domain'den bul.
  if (email && before?.email?.toLowerCase() !== email) {
    const owner = await findPersonByEmail(dir, email, personId);
    if (owner) {
      await mergePeople(personId, owner.id);
      result.mergedWith = { id: owner.id, name: owner.name };
      // Birleşince boş bırakılan alanlar diğer kayıttan gelen bilgiyi silmesin.
      if (values.role === null) delete values.role;
      if (companyId === null) companyId = undefined;
    }

    // E-postası yeni girilen kişinin şirketi domain'den bulunur (takvimdeki gibi).
    if (!companyId) {
      const { data: current } = await supabase.from('people').select('company_id').eq('id', personId).maybeSingle();
      if (!current?.company_id) {
        const domain = companyDomainFromEmail(email);
        const found = domain ? await ensureCompanyForDomain(dir, domain) : null;
        if (found) companyId = found;
      }
    }
  }

  const update = {
    ...(await personColumns(dir, personId, values)),
    ...(companyId !== undefined ? { company_id: companyId } : {}),
  };
  if (Object.keys(update).length === 0) return result;
  const { error } = await supabase.from('people').update(update).eq('id', personId);
  if (error) {
    throw new Error(
      error.code === '23505'
        ? t('Bu e-posta başka bir kişide kayıtlı.', 'That email already belongs to another person.')
        : t(`Kişi güncellenemedi: ${error.message}`, `Could not update the person: ${error.message}`)
    );
  }

  // Katılımcı satırlarındaki görünen ad ve e-posta da güncel kalsın.
  await updateParticipantsOf(dir, personId, { name: values.name, email: values.email });
  return result;
}

/**
 * Kişinin katılımcı satırlarını günceller. Katılımcı alanlarının şifre bağlamı toplantı
 * olduğu için toplantı toplantı yazılır. `personId` alanı verilirse satırlar o kişiye taşınır.
 */
async function updateParticipantsOf(
  dir: Directory,
  personId: string,
  values: { name?: string; email?: string | null; personId?: string }
): Promise<void> {
  if (values.name === undefined && values.email === undefined && values.personId === undefined) return;
  const supabase = getSupabase();
  const { data, error } = await supabase.from('meeting_participants').select('meeting_id').eq('person_id', personId);
  if (error) throw new Error(t(`Katılımcılar okunamadı: ${error.message}`, `Could not read participants: ${error.message}`));
  const meetingIds = [...new Set(((data ?? []) as Array<{ meeting_id: string }>).map((row) => row.meeting_id))];
  for (const meetingId of meetingIds) {
    const sealer = await contentSealer(dir.userId, meetingId);
    const update: Record<string, unknown> = {};
    if (values.personId !== undefined) update.person_id = values.personId;
    if (values.name !== undefined) update.name = await sealer.field('meeting_participants.name', values.name);
    if (values.email !== undefined) update.email = await sealer.field('meeting_participants.email', values.email);
    const { error: writeError } = await supabase
      .from('meeting_participants')
      .update(update)
      .eq('person_id', personId)
      .eq('meeting_id', meetingId);
    if (writeError) throw new Error(t(`Katılımcılar güncellenemedi: ${writeError.message}`, `Could not update participants: ${writeError.message}`));
  }
}

export async function createCompany(input: { name: string; domain?: string | null }): Promise<CompanyDTO> {
  const name = input.name?.trim();
  if (!name) throw new Error(t('Şirket adı gerekli.', 'A company name is required.'));
  const dir = await directory();
  const domain = input.domain?.trim().toLowerCase() || null;
  const id = crypto.randomUUID();

  const { data, error } = await getSupabase()
    .from('companies')
    .insert({ id, user_id: dir.userId, ...(await companyColumns(dir, id, { name, domain })) })
    .select('id')
    .single();
  if (error || !data) {
    throw new Error(
      error?.code === '23505'
        ? t('Bu adla ya da domain ile bir şirket zaten var.', 'A company with that name or domain already exists.')
        : t('Şirket oluşturulamadı.', 'Could not create the company.')
    );
  }
  return { id, name, domain: domain ?? undefined, peopleCount: 0, meetingCount: 0 };
}

export async function updateCompany(companyId: string, patch: CompanyUpdate): Promise<void> {
  const values: { name?: string; domain?: string | null; notes?: string | null } = {};
  if (typeof patch.name === 'string' && patch.name.trim()) values.name = patch.name.trim();
  if (patch.domain !== undefined) values.domain = patch.domain?.trim().toLowerCase() || null;
  if (patch.notes !== undefined) values.notes = patch.notes?.trim() || null;
  if (Object.keys(values).length === 0) return;

  const dir = await directory();
  const { error } = await getSupabase().from('companies').update(await companyColumns(dir, companyId, values)).eq('id', companyId);
  if (error) {
    throw new Error(
      error.code === '23505'
        ? t('Bu adla ya da domain ile bir şirket zaten var.', 'A company with that name or domain already exists.')
        : t(`Şirket güncellenemedi: ${error.message}`, `Could not update the company: ${error.message}`)
    );
  }
}

/** Aynı kişinin iki kaydını birleştirir: `dropId`'nin toplantıları `keepId`'ye geçer. */
export async function mergePeople(keepId: string, dropId: string): Promise<void> {
  if (keepId === dropId) return;
  const supabase = getSupabase();
  const dir = await directory();
  const read = async (id: string) => {
    const { data } = await supabase.from('people').select('id, name, email, role, company_id').eq('id', id).maybeSingle();
    return data ? openPersonRow(data as PersonRow, dir.userId) : null;
  };
  const [keep, drop] = await Promise.all([read(keepId), read(dropId)]);
  if (!keep || !drop) throw new Error(t('Kişi bulunamadı.', 'Person not found.'));

  const [{ data: dropRows }, { data: keepRows }] = await Promise.all([
    supabase.from('meeting_participants').select('id, meeting_id').eq('person_id', dropId),
    supabase.from('meeting_participants').select('meeting_id').eq('person_id', keepId),
  ]);
  const dropParticipants = (dropRows ?? []) as Array<{ id: string; meeting_id: string }>;
  const keepMeetings = new Set(((keepRows ?? []) as Array<{ meeting_id: string }>).map((r) => r.meeting_id));

  // İkisinin de bulunduğu toplantıda kişi iki kez listelenmesin.
  const duplicates = dropParticipants.filter((r) => keepMeetings.has(r.meeting_id)).map((r) => r.id);
  if (duplicates.length > 0) await supabase.from('meeting_participants').delete().in('id', duplicates);

  try {
    await updateParticipantsOf(dir, dropId, { personId: keepId, name: keep.name, ...(keep.email ? { email: keep.email } : {}) });
  } catch (err) {
    throw new Error(t(`Kişiler birleştirilemedi: ${(err as Error).message}`, `Could not merge the people: ${(err as Error).message}`));
  }

  // Transkriptteki konuşmacı eşlemeleri de taşınsın — alıntılar kaybolmasın.
  await repointSpeakerMaps(dir, [...new Set(dropParticipants.map((r) => r.meeting_id))], dropId, keepId);

  await supabase.from('people').delete().eq('id', dropId);
  // Eksik alanları silinen kayıttan tamamla (silindikten sonra — e-posta tekilliği çakışmasın).
  // Şifreli değerler kişiye bağlı: silinen kaydınkiler çözülüp bu kişi için yeniden şifrelenir.
  const fill: { email?: string; role?: string } = {};
  if (!keep.email && drop.email) fill.email = drop.email;
  if (!keep.role && drop.role) fill.role = drop.role;
  const update = {
    ...(await personColumns(dir, keepId, fill)),
    ...(!keep.company_id && drop.company_id ? { company_id: drop.company_id } : {}),
  };
  if (Object.keys(update).length > 0) await supabase.from('people').update(update).eq('id', keepId);
}

/** Toplantıların `options.speakerMap`'inde `fromId`'ye eşlenmiş etiketleri `toId`'ye çevirir. */
async function repointSpeakerMaps(dir: Directory, meetingIds: string[], fromId: string, toId: string): Promise<void> {
  if (meetingIds.length === 0) return;
  const supabase = getSupabase();
  const { data } = await supabase.from('meetings').select('id, options').in('id', meetingIds);
  for (const row of (data ?? []) as Array<{ id: string; options: any }>) {
    // Eşleme şifreli olabilir (3. seviye): çözülür, değiştirilir, yeniden şifrelenir.
    const opened = await openMeetingRow(row, dir.userId);
    const map = opened.options?.speakerMap as Record<string, string> | undefined;
    if (!map || !Object.values(map).includes(fromId)) continue;
    const next = Object.fromEntries(Object.entries(map).map(([label, id]) => [label, id === fromId ? toId : id]));
    const sealer = await contentSealer(dir.userId, row.id);
    const speakerMap = await sealer.json('meetings.speaker_map', next);
    const options = { ...row.options, speakerMap };
    if (options.enc && options.enc > sealer.level) options.enc = sealer.enabled ? sealer.level : undefined;
    const { error } = await supabase.from('meetings').update({ options }).eq('id', row.id);
    if (error) console.error('[directory] konuşmacı eşlemesi taşınamadı:', row.id, error.message);
  }
}

export async function deletePerson(personId: string): Promise<void> {
  const { error } = await getSupabase().from('people').delete().eq('id', personId);
  if (error) throw new Error(t(`Kişi silinemedi: ${error.message}`, `Could not delete the person: ${error.message}`));
}

/** Kişi adlarını kimlikten çözer — toplantılardaki konuşmacı eşlemesi için. */
export async function getPeopleNames(personIds: string[]): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  if (personIds.length === 0) return names;
  const userId = await requireUserId();
  const { data } = await getSupabase().from('people').select('id, name').in('id', personIds);
  for (const row of (data ?? []) as Array<{ id: string; name: string }>) {
    names.set(row.id, (await openPersonRow(row, userId)).name);
  }
  return names;
}
